import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment as ENV } from '../../environments/environment';
import {
    ILessonObservationEventType, ILessonObservationEvent, ILessonObservationSearch,
    ILessonObservationStructure, ILessonObservationEditModel, ILessonObservation, ILessonObservationListItem, ILessonObservationObserver
} from '../models/LessonObservation';
import { Utils } from '../core/Utils';
import { IPagingParams } from '../models/Common';

/**
 * Lesson observation service
 */
@Injectable({ providedIn: "root" })
export class LessonObservationService {
    constructor(private http: HttpClient) { }

    readonly apiUrl = `${ENV.apiUrl}/lessonObservations`;

    /**
     * Get lesson observations.
     * @returns
     */
    get(paging?: IPagingParams): Observable<{ items: ILessonObservationListItem[], total: number }> {
        return this.http.get<{ items: ILessonObservationListItem[], total: number }>(this.apiUrl, {
            params: Utils.sanitizeQueryParams(paging)
        });
    }

    /**
     * Get a lesson observation.
     * @param id
     * @returns
     */
    getById(id: number): Observable<ILessonObservation> {
        const url = `${this.apiUrl}/${id}`;
        return this.http.get<ILessonObservation>(url);
    }

    /**
     * Get event types.
     */
    getEventTypes(): Observable<ILessonObservationEventType[]> {
        const url = `${this.apiUrl}/eventTypes`;
        return this.http.get<ILessonObservationEventType[]>(url);
    }

    /**
     * Get events.
     * @param search
     * @returns
     */
    getEvents(search: ILessonObservationSearch): Observable<ILessonObservationEvent[]> {
        const url = `${this.apiUrl}/events`;
        const params = Utils.sanitizeQueryParams({
            ...search,
            Date: Utils.getInputDateString(search.Date)
        });

        return this.http.get<ILessonObservationEvent[]>(url, { params });
    }

    /**
     * Get person structures.
     * @param upn
     * @returns
     */
    getStructures(upn: string): Observable<ILessonObservationStructure[]> {
        const url = `${this.apiUrl}/structures`;
        return this.http.get<ILessonObservationStructure[]>(url, { params: { upn } });
    }

    /**
     * Get available observers.
     * @param upn
     * @returns
     */
    getObservers(): Observable<ILessonObservationObserver[]> {
        const url = `${this.apiUrl}/observers`;
        return this.http.get<ILessonObservationObserver[]>(url);
    }

    /**
     * Create a lesson observation.
     * @param data
     * @returns
     */
    create(data: ILessonObservationEditModel): Observable<number> {
        return this.http.post<number>(this.apiUrl, data);
    }

    /**
     * Update a lesson observation.
     * @param id
     * @param data
     * @returns
     */
    update(id: number, data: ILessonObservationEditModel): Observable<any> {
        const url = `${this.apiUrl}/${id}`;
        return this.http.put(url, data);
    }

    /**
     * Delete a lesson observation.
     */
    delete(id: number): Observable<any> {
        const url = `${this.apiUrl}/${id}`;
        return this.http.delete(url);
    }

    /**
     * Promote a lesson observation.
     */
    promote(id: number): Observable<any> {
        const url = `${this.apiUrl}/${id}/promote`;
        return this.http.post<any>(url, null);
    }

    /**
     * Approve a lesson observation.
     */
    approve(id: number): Observable<any> {
        const url = `${this.apiUrl}/${id}/approve`;
        return this.http.post<any>(url, null);
    }

    /**
     * Reject a lesson observation.
     */
    reject(id: number, comment: string): Observable<any> {
        const url = `${this.apiUrl}/${id}/reject`;
        return this.http.post<any>(url, { comment });
    }
}
